import React, { useContext, useEffect, useState } from 'react';
import { AgFinancingPhase4Status, AgGroup } from '@indigo-ag/schema';
import { Button, LegalBody, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';

import { changeCessionGroupStatus } from '../../../../brazil-grower/queries';
import { DecisionModal } from '../../../../brazil-grower/components/BrazilGrowerFinance/shared/DecisionModal/DecisionModal';
import { haveToSendToFund } from '../../BrazilGrowerFinance/utils/cessionGroup.util';
import { text, WithConditionalTooltip } from 'shared';
import { UserContext } from '../../../../../core/components/GateWay/GateWay';
import i18next from 'core/i18n';

interface IGroupAction {
  actionButtonIcon?: IconDefinition;
  actionButtonText: string;
  actionSuccessfulMessage: string;
  nextStatus: AgFinancingPhase4Status;
  validation?: {
    messageOfError: string;
    queryMessageOfError?: string;
    isValid: (group: AgGroup) => boolean;
  };
  modal: {
    title: string;
    body: string;
  };
  secondaryAction?: Omit<IGroupAction, 'secondaryAction'>;
}

interface CessionActionButtonProps {
  agGroup: AgGroup;
}

export const CessionActionButton: React.FunctionComponent<CessionActionButtonProps> = ({ agGroup }) => {
  /**
   * Context
   */
  const { isCreditLead, isCreditAnalyst, isSuper } = useContext(UserContext);

  const sendToManagerAction: IGroupAction = {
    actionButtonText: i18next.t('sendToManagerApproval'),
    actionSuccessfulMessage: text.firstLetterUppercase(i18next.t('sendToManagerApprovalSuccessfully')),
    nextStatus: AgFinancingPhase4Status.AWAITING_MANAGER_APPROVAL,
    modal: {
      title: i18next.t('sendToManagerApproval'),
      body: i18next.t('sendToManagerApprovalMessage')
    },
    validation: {
      messageOfError: i18next.t('sendToManagerApprovalCondition'),
      isValid: (group: AgGroup) => !!group.fundInfo
    }
  };

  let actions: { [key in AgFinancingPhase4Status]?: IGroupAction } = {
    [AgFinancingPhase4Status.AWAITING_ASSIGNMENT]: {
      actionButtonText: i18next.t('startCession'),
      actionSuccessfulMessage: text.firstLetterUppercase(i18next.t('cessionStartedSuccessfully')),
      nextStatus: AgFinancingPhase4Status.ASSIGNED_TO_FUND,
      modal: {
        title: i18next.t('startingCession'),
        body: i18next.t('startingCessionMessage')
      },
      validation: {
        queryMessageOfError: text.firstLetterUppercase(i18next.t('fundInfoNotUpdatedAPI')),
        messageOfError: i18next.t('startCessionCondition'),
        isValid: (group: AgGroup) => !!group.agFinancingProcesses.length && haveToSendToFund(group)
      }
    },
    [AgFinancingPhase4Status.ASSIGNED_TO_FUND]: {
      actionButtonText: i18next.t('fundInformationReceived'),
      actionSuccessfulMessage: text.firstLetterUppercase(i18next.t('fundInformationReceivedSuccessfully')),
      nextStatus: AgFinancingPhase4Status.RECEIVED_BY_FUND,
      modal: {
        title: i18next.t('fundInformationReceived'),
        body: i18next.t('fundInformationReceivedMessage')
      }
    }
  };

  if (isCreditAnalyst || isSuper) {
    actions = {
      ...actions,
      [AgFinancingPhase4Status.RECEIVED_BY_FUND]: sendToManagerAction,
      [AgFinancingPhase4Status.DENIED]: sendToManagerAction
    };
  }

  if (isCreditLead || isSuper) {
    actions = {
      ...actions,
      [AgFinancingPhase4Status.AWAITING_MANAGER_APPROVAL]: {
        actionButtonIcon: faThumbsUp,
        actionButtonText: text.firstLetterUppercase(i18next.t('approve')),
        actionSuccessfulMessage: text.firstLetterUppercase(`${i18next.t('cession')} ${i18next.t('approved')}`),
        nextStatus: AgFinancingPhase4Status.SUBMITTED_MINUTA,
        modal: {
          title: text.firstLetterUppercase(`${i18next.t('approving')} ${i18next.t('cession')}`),
          body: i18next.t('approvingCessionMessage')
        },
        secondaryAction: {
          actionButtonIcon: faThumbsDown,
          actionButtonText: text.firstLetterUppercase(i18next.t('deny')),
          actionSuccessfulMessage: text.firstLetterUppercase(`${i18next.t('cession')} ${i18next.t('denied')}`),
          nextStatus: AgFinancingPhase4Status.DENIED,
          modal: {
            title: text.firstLetterUppercase(`${i18next.t('denying')} ${i18next.t('cession')}`),
            body: text.firstLetterUppercase(i18next.t('denyingCessionMessage'))
          }
        }
      }
    };
  }

  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isDecisionSecondaryOpen, setIsDecisionSecondaryOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<IGroupAction | undefined>();
  const [isValidToMoveNextStatus, setIsValidToMoveNextStatus] = useState(true);

  /**
   * Mutations
   */
  const [changeCessionGroupStatusFn] = useMutation(changeCessionGroupStatus);

  useEffect(() => {
    if (agGroup?.status && actions[agGroup.status]) {
      setCurrentAction(actions[agGroup.status]);
    } else {
      setCurrentAction(undefined);
    }
  }, [agGroup.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentAction) {
      setIsValidToMoveNextStatus(currentAction.validation ? currentAction.validation.isValid(agGroup) : true);
    }
  }, [agGroup, currentAction]);

  const { addFlashMessage } = useFlashMessage();

  const confirmSecondaryDecision = (notes: string) => {
    return changeStatus(notes, currentAction?.secondaryAction);
  };

  const confirmDecision = (notes: string) => {
    return changeStatus(notes, currentAction);
  };

  const changeStatus = (notes: string, action?: Omit<IGroupAction, 'secondaryAction'>) => {
    return changeCessionGroupStatusFn({
      variables: {
        agGroupId: agGroup.id,
        status: action!.nextStatus,
        notes
      }
    })
      .then(response => {
        setIsDecisionOpen(false);

        if (haveToSendToFund(response?.data?.changeCessionGroupStatus, false)) {
          addFlashMessage({
            content: action!.validation!.queryMessageOfError || i18next.t('error'),
            title: '',
            variant: 'error'
          });
        } else {
          addFlashMessage({
            content: action!.actionSuccessfulMessage,
            title: '',
            variant: 'success'
          });
        }
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: action!.validation!.queryMessageOfError || e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const closeSecondaryDecisionModal = () => {
    setIsDecisionSecondaryOpen(false);
  };

  const takeSecondaryDecision = () => {
    setIsDecisionSecondaryOpen(true);
  };

  const closeDecisionModal = () => {
    setIsDecisionOpen(false);
  };

  const takeDecision = () => {
    setIsDecisionOpen(true);
  };

  return (
    <>
      {currentAction ? (
        <>
          {!!currentAction.secondaryAction && (
            <>
              <Button
                size="small"
                className="text-transform-capitalize"
                onClick={takeSecondaryDecision}
                variant="secondary"
                faIconAfter={currentAction.secondaryAction.actionButtonIcon}
              >
                {currentAction.secondaryAction!.actionButtonText}
              </Button>
              <DecisionModal
                open={isDecisionSecondaryOpen}
                title={currentAction.secondaryAction.modal.title}
                message={currentAction.secondaryAction.modal.body}
                closeModal={closeSecondaryDecisionModal}
                onConfirmAction={confirmSecondaryDecision}
              />
            </>
          )}
          <WithConditionalTooltip
            visible={!isValidToMoveNextStatus}
            title={
              <LegalBody className="text-transform-uppercase-first-letter">
                {currentAction!.validation?.messageOfError}
              </LegalBody>
            }
            placement="top"
          >
            <Button
              size="small"
              className="text-transform-capitalize"
              onClick={takeDecision}
              disabled={!isValidToMoveNextStatus}
              variant="primary"
              faIconAfter={currentAction.actionButtonIcon}
            >
              {currentAction!.actionButtonText}
            </Button>
          </WithConditionalTooltip>
          <DecisionModal
            open={isDecisionOpen}
            title={currentAction.modal.title}
            message={currentAction.modal.body}
            closeModal={closeDecisionModal}
            onConfirmAction={confirmDecision}
          />
        </>
      ) : (
        undefined
      )}
    </>
  );
};
